import {Event} from 'shared/events/Event';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIsConfirmationPage} from 'components/customer/Confirmation/helpers/useIsConfirmationPage';
import {useCheckProductVariations} from 'components/customer/Confirmation/helpers/useCheckProductVariations';
import {useNotificationContext} from 'contexts';
import {genericMessageHandler} from 'shared/helpers';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {usePaymentSettings} from 'components/customer/Confirmation/helpers/usePaymentSettings';
import {DispatchMethod} from 'hooks/Job/Job';
import {useAppSelector} from 'store/customer';
import {selectHasCustomerOverpaid} from 'components/customer/Confirmation/store/confirmationSlice';
import {useCurrentDispatchMethod} from 'components/customer/Job/helpers/useCurrentDispatchMethods';

export const useGoToPaymentPage = (): Event => {
    const {notify} = useNotificationContext();
    const navigate = useNavigate();
    const {job} = useJobContext() as PartialJob;
    const {isConfirmationPage} = useIsConfirmationPage();
    const {getPaymentSettings} = usePaymentSettings();
    const overPaid = useAppSelector(selectHasCustomerOverpaid);

    const dispatchMethod = useCurrentDispatchMethod();

    const {checkIfThereAreVariations} = useCheckProductVariations();

    const action = useCallback(async (): Promise<boolean> => {
        if (overPaid) return true;

        if (isConfirmationPage) {
            try {
                const hasVariation = await checkIfThereAreVariations();
                const hasFreight = dispatchMethod != DispatchMethod.PICKUP;

                if (
                    (hasVariation || hasFreight) &&
                    job.status == JOB_STATUS.PROCESSING
                ) {
                    return true;
                }

                const paymentSettings = await getPaymentSettings(20000);

                if (
                    paymentSettings?.allowPayOnAccount ||
                    paymentSettings?.enableCardPayments ||
                    paymentSettings?.enableInvoicePayments
                ) {
                    void navigate(`/v2/job/${job.displayId}/payment`);
                    return false;
                }
            } catch (e) {
                if (e instanceof Error) {
                    genericMessageHandler(notify, e.message);
                }
                return false;
            }
        }

        return true;
    }, [
        job,
        navigate,
        isConfirmationPage,
        checkIfThereAreVariations,
        overPaid,
        dispatchMethod,
    ]);

    return {
        action,
    };
};
